/**
 * Custom Habit Reminders Local Notifications
 * 
 * Schedules local notifications for habit reminders based on user preferences.
 */

import { Capacitor } from '@capacitor/core'
import type { ScheduleOptions } from '@capacitor/local-notifications'
import type { Habit } from '@/types/database'

type LNPlugin = typeof import('@capacitor/local-notifications').LocalNotifications

let _cachedLNResult: { plugin: LNPlugin } | null | undefined

async function getLocalNotifications(): Promise<{ plugin: LNPlugin } | null> {
  if (_cachedLNResult !== undefined) return _cachedLNResult
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    _cachedLNResult = { plugin: LocalNotifications }
  } catch (e) {
    console.warn('[HabitNotifications] LocalNotifications plugin unavailable:', e)
    _cachedLNResult = null
  }
  return _cachedLNResult
}

// Ensure exact alarm permission on Android 12+
async function ensureExactAlarmPermission(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'android') return true

  const result = await getLocalNotifications()
  if (!result) return false
  const LN = result.plugin

  try {
    const setting = await LN.checkExactNotificationSetting()
    if (setting.exact_alarm === 'granted') return true

    console.warn('[HabitNotifications] Exact alarm not granted, opening settings')
    const changed = await LN.changeExactNotificationSetting()
    return changed.exact_alarm === 'granted'
  } catch {
    return true
  }
}

/**
 * Generate a consistent, unique integer ID for a habit notification.
 * Since a habit can have multiple days, we add an offset based on the day.
 * Day 0-6 (Sun-Sat) will be added to a base hash of the habit ID.
 * The daily one just uses the base hash.
 */
function getNotificationId(habitId: string, dayIndex: number = 0): number {
  let hash = 0
  for (let i = 0; i < habitId.length; i++) {
    hash = Math.imul(31, hash) + habitId.charCodeAt(i) | 0
  }
  // Ensure it's a positive 32-bit integer that fits in typical notification bounds
  // Use a base prefix to avoid colliding with pomodoro or others, say 200000
  return Math.abs(hash % 100000) + 200000 + dayIndex
}

/**
 * Schedule custom reminders for a given habit.
 * This function will cancel any existing reminders for this habit,
 * and if enabled, schedule new ones based on the frequency type.
 */
export async function scheduleHabitReminders(habit: Habit): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  try {
    const lnResult = await getLocalNotifications()
    if (!lnResult) return
    const LN = lnResult.plugin

    // Always cancel existing notifications for this habit first to reset state
    await cancelHabitReminders(habit.id, LN)

    if (!habit.reminder_enabled || !habit.reminder_time) {
      return // Reminders disabled or no time set
    }

    // Check permissions
    const permResult = await LN.checkPermissions()
    if (permResult.display !== 'granted') {
      const reqResult = await LN.requestPermissions()
      if (reqResult.display !== 'granted') {
        console.error('[HabitNotifications] Permission denied')
        return
      }
    }

    await ensureExactAlarmPermission()

    // Parse the time (format: "HH:MM")
    const [hourStr, minuteStr] = habit.reminder_time.split(':')
    const hour = parseInt(hourStr, 10)
    const minute = parseInt(minuteStr, 10)

    const title = `Time for: ${habit.title}`
    const body = `Don't forget to complete your habit! ${habit.emoji || '🎯'}`
    const isAndroid = Capacitor.getPlatform() === 'android'
    const channelId = isAndroid ? 'miac_default' : undefined

    const notificationsToSchedule = []

    if (habit.frequency_type === 'specific_days' && habit.schedule_days && habit.schedule_days.length > 0) {
      // Schedule for each specific day
      // Note: Capacitor LocalNotifications uses 1-7 for Sun-Sat
      // Our DB stores 0-6 for Sun-Sat. So we add 1.
      for (const dbDay of habit.schedule_days) {
        const capacitorDay = dbDay + 1
        notificationsToSchedule.push({
          id: getNotificationId(habit.id, capacitorDay),
          title,
          body,
          schedule: {
            on: {
              weekday: capacitorDay,
              hour,
              minute,
            },
            allowWhileIdle: true,
          },
          ...(channelId && { channelId }),
          extra: { habitId: habit.id },
        })
      }
    } else {
      // Daily/Weekly/Monthly/Yearly - just remind every day at the given time
      notificationsToSchedule.push({
        id: getNotificationId(habit.id, 0),
        title,
        body,
        schedule: {
          every: 'day' as const,
          on: {
            hour,
            minute,
          },
          allowWhileIdle: true,
        },
        ...(channelId && { channelId }),
        extra: { habitId: habit.id },
      })
    }

    if (notificationsToSchedule.length > 0) {
      await LN.schedule({ notifications: notificationsToSchedule })
      console.log(`[HabitNotifications] Scheduled ${notificationsToSchedule.length} reminder(s) for habit ${habit.id} at ${habit.reminder_time}`)
    }

  } catch (error) {
    console.error('[HabitNotifications] Failed to schedule reminders:', error)
  }
}

/**
 * Cancel all reminders for a specific habit
 */
export async function cancelHabitReminders(habitId: string, LN?: LNPlugin): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    if (!LN) {
      const lnResult = await getLocalNotifications()
      if (!lnResult) return
      LN = lnResult.plugin
    }

    const idsToCancel = []
    // Cancel the daily one
    idsToCancel.push({ id: getNotificationId(habitId, 0) })
    // Cancel the potential day-specific ones (1 to 7)
    for (let i = 1; i <= 7; i++) {
      idsToCancel.push({ id: getNotificationId(habitId, i) })
    }

    await LN.cancel({ notifications: idsToCancel })
  } catch (error) {
    console.warn('[HabitNotifications] Failed to cancel reminders:', error)
  }
}

/**
 * Sync all habit reminders with the database state.
 * Usually called on app initialization or after syncing data.
 */
export async function syncAllHabitReminders(habits: Habit[]): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const lnResult = await getLocalNotifications()
    if (!lnResult) return
    const LN = lnResult.plugin

    console.log('[HabitNotifications] Syncing all habit reminders...')
    
    // For simplicity, we just cancel and reschedule for all active habits
    // Ideally we would fetch pending, but this ensures a clean state
    for (const habit of habits) {
      if (habit.is_archived || habit.conquered_at) {
        await cancelHabitReminders(habit.id, LN)
      } else {
        await scheduleHabitReminders(habit)
      }
    }
  } catch (error) {
    console.error('[HabitNotifications] Failed to sync all reminders:', error)
  }
}
